import { useEffect, useRef, useState } from "react";

import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Icon } from "@/DesignSystem";
import { Tag } from "@/DesignSystem/Molecules/Tag/Tag";
import AIScribeAction from "../Api/AIScribeAction";
import { AIScribeConsultationNoteModel } from "../Models/AIScribeConsultationNoteModel";
import { AIScribePopupContent } from "./AIScribePopupContent";

import styles from "./AIScribe.module.scss";

import ArrowsInRegular from "@/DesignSystem/Atoms/Icons/Types/ArrowsIn/arrows-in-regular.svg";
import ArrowsOutRegular from "@/DesignSystem/Atoms/Icons/Types/ArrowsOut/arrows-out-regular.svg";
import MicrophoneSlashRegularIcon from "@/DesignSystem/Atoms/Icons/Types/MicrophoneSlash/microphone-slash-regular.svg";
import MinusRegular from "@/DesignSystem/Atoms/Icons/Types/Minus/minus-regular.svg";
import PauseFill from "@/DesignSystem/Atoms/Icons/Types/Pause/pause-fill.svg";
import RecordFill from "@/DesignSystem/Atoms/Icons/Types/Record/record-fill.svg";
import SpeakerSimpleSlashRegularIcon from "@/DesignSystem/Atoms/Icons/Types/SpeakerSimpleSlash/speaker-simple-slash-regular.svg";
import XRegular from "@/DesignSystem/Atoms/Icons/Types/X/x-regular.svg";

interface AIScribePopupProps {
    consultationId: string;
    url: string;
    openBottomPos: number,
    openRightPos: number,
    isRecording: boolean;
    recordingDuration: number;

    onClose: () => void;
    onNoteSyncCompleted: (data: AIScribeConsultationNoteModel) => void;
    onNoteSyncFailed: (reason: string) => void;
    onRecordingStarted: () => void;
    onRecordingStopped: (duration: number) => void;
}

function AIScribePopup(props: Readonly<AIScribePopupProps>): JSX.Element {
    const { getText } = useTranslation();
    const [showContent, setShowContent] = useState(true);
    const [text, setText] = useState(
        {
            title: getText("AIScribe_AIAssistant"),
            recording: getText("AIScribe_Recording"),
            paused: getText("AIScribe_Pause"),
            noSound: getText("AIScribe_NoSoundDetected"),
            micIssues: getText("AIScribe_MicrophoneIssues")
        }
    );
    const [soundDetection, setSoundDetection] = useState(true);
    const [microphoneBlockedError, setMicrophoneBlockedError] = useState(false);
    const [microphoneDetectionIncomplete, setMicrophoneDetectionIncomplete] = useState(false);
    const [isMaximized, setIsMaximized] = useState<boolean>(false);
    const action = new AIScribeAction();
    const popupRef = useRef(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    async function handleNoteGenerated(): Promise<void> {
        let response = await action.synchronizeAsync(props.consultationId);
        props.onNoteSyncCompleted?.(AIScribeConsultationNoteModel.fromDto(response));
    }

    function handleOnPopupClose(): void {
        props.onClose?.();
    }

    function handleMinimize(): void {
        setShowContent(!showContent);
        onSizeChanged(false);
    }

    function handleMaximize(): void {
        const windowHeight = window?.innerHeight;

        if (!windowHeight) {
            return;
        }

        const message = {
            type: "maximize_to_height",
            height: Math.ceil(windowHeight * 0.8),
            source: "ai_scribe_integration"
        };
        iframeRef.current?.contentWindow?.postMessage(message, new URL(props.url).origin);
    }

    function handleUnmaximize(): void {
        const message = {
            type: "unmaximize",
            source: "ai_scribe_integration"
        };
        iframeRef.current?.contentWindow?.postMessage(message, new URL(props.url).origin);
    }

    function onSizeChanged(isResize: boolean = false): void {
        function resizeCallback(): void {
            if (popupRef.current === null) {
                return;
            }

            // get the size of the window and move into viewport if its exceeded
            const selfContainer = $(popupRef.current);
            const container = $(window);
            const width = selfContainer.outerWidth();
            const height = selfContainer.outerHeight();
            const { left, top } = selfContainer.offset();
            const containerWidth = container.outerWidth();
            const containerHeight = container.outerHeight();

            // coordinates are bottom right offsets which are 0,0 at the bottom right
            let right = containerWidth - left - width;
            let bottom = containerHeight - top - height;
            let outOfBounds = false;

            if (right < 0) {
                right = 0;
                outOfBounds = true;
            }
            else if (right + width > containerWidth) {
                right = Math.max(0, containerWidth - width);
                outOfBounds = true;
            }

            if (bottom < 0) {
                bottom = 0;
                outOfBounds = true;
            }
            else if (bottom + height > containerHeight) {
                bottom = Math.max(0, containerHeight - height);
                outOfBounds = true;
            }

            if (outOfBounds) {
                selfContainer
                    .css("top", "auto")
                    .css("left", "auto")
                    .css("right", (right) + "px")
                    .css("bottom", (bottom) + "px");
            }
        }

        if (isResize) {
            resizeCallback();
        }
        else {
            // hack to resize after the react component finishes drawing
            setTimeout(resizeCallback, 50);
        }
    }

    // detect browser size change to keep popup within window
    useEffect(() => {
        function onResize(): void {
            onSizeChanged(true);
        }

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    });

    // add dragging support to window
    useEffect(() => {
        if (popupRef.current === null || headerRef.current === null) {
            return;
        }

        const draggableElement = $(popupRef.current);
        const handle = $(headerRef.current);

        function onStartDrag(): void {
            // convert to top left alignment for dragging
            const { left, top } = draggableElement.offset();

            draggableElement
                .css("bottom", "auto")
                .css("right", "auto")
                .css("left", left + "px")
                .css("top", top + "px");

            handle.addClass(styles.headerDragging);
        }

        function onStopDrag(): void {
            // convert back to bottom right alignment
            const containerElem = $(window);
            const { left, top } = draggableElement.offset();
            const width = draggableElement.outerWidth();
            const height = draggableElement.outerHeight();
            const containerWidth = containerElem.outerWidth();
            const containerHeight = containerElem.outerHeight();
            const right = containerWidth - width - left;
            const bottom = containerHeight - height - top;

            draggableElement
                .css("top", "auto")
                .css("left", "auto")
                .css("right", (right) + "px")
                .css("bottom", (bottom) + "px");

            handle.removeClass(styles.headerDragging);

            // If you're dragging while MYLE redraws the main window, dragging will break unless we reinitialize the draggable.
            window.setTimeout(() => {
                try {
                    draggableElement.draggable(dragOptions);
                }
                catch { }
            }, 50);
        }

        const dragOptions = {
            containment: "window",
            handle: handle,
            start: onStartDrag,
            stop: onStopDrag
        };

        draggableElement.draggable(dragOptions);

        return () => {
            // MYLE redraws Main randomly and quickly, thus redrawing this component multiple times as well. Sometimes this causes the draggable to be destroyed before its initialized, which throws an error.
            try { draggableElement.draggable("destroy"); }
            catch { }
        };
    });

    useOnStoreChange(
        settingsStore,
        () => setText({
            title: getText("AIScribe_AIAssistant"),
            recording: getText("AIScribe_Recording"),
            paused: getText("AIScribe_Pause"),
            noSound: getText("AIScribe_NoSoundDetected"),
            micIssues: getText("AIScribe_MicrophoneIssues")
        })
    );

    function renderStatus(): JSX.Element {
        if (microphoneBlockedError || microphoneDetectionIncomplete) {
            return (
                <Tag label={text.micIssues} variant="error" size="sm">
                    <MicrophoneSlashRegularIcon />
                </Tag>
            );
        }
        else if (props.isRecording) {
            if (!soundDetection) {
                return (
                    <Tag label={text.noSound} variant="warning" size="sm">
                        <SpeakerSimpleSlashRegularIcon />
                    </Tag>
                );
            }

            return (
                <>
                    <Icon size="sm" colorVariant="error"><RecordFill /></Icon>
                    <span className={styles.label}>{text.recording}</span>
                </>
            );
        }
        else if (props.recordingDuration > 0) {
            return (
                <>
                    <Icon size="sm"><PauseFill /></Icon>
                    <span className={styles.label}>{text.paused}</span>
                </>
            );
        }

        return null;
    }

    return (
        <div ref={popupRef} id="ai-scribe-popup" data-cy="ai-scribe-popup" className={styles.aiscribePopup} style={{ right: props.openRightPos, bottom: props.openBottomPos }}>
            <div ref={headerRef} className={styles.header}>
                <div className={styles.content}>
                    <span className={styles.title}>{text.title}</span>
                    <div className={styles.status}>
                        {renderStatus()}
                    </div>
                </div>
                <div className={styles.headerButtons}>
                    {isMaximized ?
                        (
                            <button id="ai-scribe-popup-unmaximize" className={styles.headerButton} onClick={handleUnmaximize} tabIndex={0}>
                                <Icon size="sm"><ArrowsInRegular /></Icon>
                            </button>
                        ) : (
                            <button id="ai-scribe-popup-maximize" className={styles.headerButton} onClick={handleMaximize} tabIndex={0}>
                                <Icon size="sm"><ArrowsOutRegular /></Icon>
                            </button>
                        )
                    }
                    <button id="ai-scribe-popup-minimize" className={styles.headerButton} onClick={handleMinimize} tabIndex={0}>
                        <Icon size="sm"><MinusRegular /></Icon>
                    </button>
                    <button id="ai-scribe-popup-close" className={styles.headerButton} onClick={handleOnPopupClose} tabIndex={0}>
                        <Icon size="sm"><XRegular /></Icon>
                    </button>
                </div>
            </div>
            <AIScribePopupContent
                iframeRef={iframeRef}
                consultationId={props.consultationId}
                url={props.url}
                onNoteGenerated={handleNoteGenerated}
                onNoteGeneratedFailed={(reason: string) => props.onNoteSyncFailed(reason)}
                onRecordingStarted={props.onRecordingStarted}
                onRecordingStopped={(duration: number) => props.onRecordingStopped(duration)}
                onSizeChanged={() => onSizeChanged()}
                isVisible={showContent}
                onSoundDetection={setSoundDetection}
                onMicrophoneBlocked={() => setMicrophoneBlockedError(true)}
                onMicrophoneDetectionIncomplete={() => setMicrophoneDetectionIncomplete(true)}
                onMicrophoneDeviceDetected={() => {
                    setMicrophoneBlockedError(false);
                    setMicrophoneDetectionIncomplete(false);
                }}
                onMaximizeChanged={(isMaximized: boolean) => {
                    setIsMaximized(isMaximized);
                    setShowContent(true);   // Ensure content is shown when maximized even if it was minimized before
                }}
            />
        </div>
    );
}

export { AIScribePopup };
